import { format } from "date-fns";

import { Chip } from "@mui/material";

import { CLIENTS, OPERATIONS, getChipColor } from "../../../constants/value";
import { dateSortComparator } from "../../../utils/date";

const textColumn = (field, headerName, minWidth = 120) => ({
  field,
  headerName,
  minWidth,
  flex: 2,
  sortable: false,
});

const dateColumn = (field, headerName) => ({
  field,
  headerName,
  minWidth: 200,
  filterable: false,
  flex: 3,
  sortable: false,
  valueGetter: (params) => params.row[field] || null,
  valueFormatter: (params) =>
    params.value && `${format(new Date(params.value), "MMMM d, y hh:mm a")}`,
  sortComparator: dateSortComparator,
});

/**
 * Identifier columns shown between ID and batch metadata, keyed by operation.
 * Falls back to carrier-specific defaults when no operation override exists.
 */
const IDENTIFIER_COLUMNS_BY_OPERATION = {
  [OPERATIONS.change_number]: [
    textColumn("old_msisdn", "Old MDN"),
    textColumn("new_msisdn", "New MDN"),
  ],
  [OPERATIONS.update_number]: [textColumn("msisdn", "MDN")],
};

const shouldShowIccid = (operation, client) => {
  if (operation === OPERATIONS.update_number) return false;
  if (operation === OPERATIONS.change_number && client === CLIENTS.tmobilej) {
    return false;
  }
  return true;
};

const resolveIdentifierColumns = (operation, client) => {
  if (IDENTIFIER_COLUMNS_BY_OPERATION[operation]) {
    return IDENTIFIER_COLUMNS_BY_OPERATION[operation];
  }
  if (client === CLIENTS.tmobilej) {
    return [textColumn("msisdn", "MDN")];
  }
  return [];
};

export const getSearchCopyKeys = (operation) => {
  const keysByOperation = {
    [OPERATIONS.change_number]: [
      "id",
      "iccid",
      "old_msisdn",
      "new_msisdn",
      "batch_id",
      "status",
      "created_at",
      "last_updated",
    ],
    [OPERATIONS.update_number]: [
      "id",
      "msisdn",
      "batch_id",
      "status",
      "created_at",
      "last_updated",
    ],
  };

  return (
    keysByOperation[operation] ?? [
      "id",
      "iccid",
      "msisdn",
      "batch_id",
      "status",
      "created_at",
      "last_updated",
    ]
  );
};

export const buildSearchListColumns = ({ operation, client, isImsi }) => {
  const identifierField = isImsi ? "imsi" : "iccid";
  const identifierColumns = resolveIdentifierColumns(operation, client);

  return [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    ...identifierColumns,
    ...(shouldShowIccid(operation, client)
      ? [textColumn(identifierField, isImsi ? "IMSI" : "ICCID", 200)]
      : []),
    {
      field: "batch_id",
      headerName: "Batch ID",
      minWidth: 300,
      flex: 3,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={getChipColor(params.row.status)}
        />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];
};
